import {csrfToken} from './api';

/**
 * Client for ChunkedUploadSessionController (Issue #192 Docket #37, Stage 1b -- the backend
 * primitive itself, PR #274, is Stage 1a). Lets a document larger than the existing single-shot
 * `FENZSAFE_DOCUMENT_MAX_BYTES` cap upload in pieces via `/api/v1/fenzsafe/documents/{id}/
 * upload-sessions` (start) -> `/api/v1/fenzsafe/upload-sessions/{id}/chunks` (append, one call per
 * slice) -> `/api/v1/fenzsafe/upload-sessions/{id}/complete` (commit), reusing the exact same
 * scan/accept/quarantine pipeline `registerDocumentBytes()` already goes through -- see
 * ChunkedUploadSessionController's own doc comment.
 *
 * Deliberately NOT wired into any FenzSafeApp.tsx call site in this slice: that file currently has
 * two other lanes actively landing on it (Docket #37's own EML viewer, PR #279, and the guided
 * email-file import, PR #285), and this is a purely additive, self-contained module with zero
 * import from fenzsafeStore.ts/fenzsafeBackendAdapter.ts, so it cannot conflict with either. Wiring
 * this into the upload UI (replacing the flat "too large" rejection at
 * `FENZSAFE_DOCUMENT_MAX_BYTES`) is the next bounded follow-up slice once those land.
 */

export class FenzSafeChunkedUploadError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    /** Set once a session exists, so a caller can retry commit() on a scanner-outage 503 without re-uploading -- see retryCommitUploadSession() below. */
    public readonly uploadSessionId?: string,
  ) {
    super(message);
    this.name = 'FenzSafeChunkedUploadError';
  }
}

/** Same per-request cap ChunkedUploadSessionController::MAX_CHUNK_BYTES enforces server-side. */
export const FENZSAFE_CHUNK_MAX_BYTES = 10 * 1024 * 1024;

export type ChunkedUploadProgress = {sentBytes: number; totalBytes: number};

type ChunkedUploadOptions = {
  /** Defaults to FENZSAFE_CHUNK_MAX_BYTES; only ever lowered by a caller, never raised past it. */
  chunkBytes?: number;
  onProgress?: (progress: ChunkedUploadProgress) => void;
  expectedScope?: string;
  signal?: AbortSignal;
};

/** True whenever a file needs this module instead of the existing single-shot registerDocumentBytes() path. */
export function needsChunkedUpload(fileSizeBytes: number, singleShotMaxBytes: number): boolean {
  return fileSizeBytes > singleShotMaxBytes;
}

/**
 * Issue #192 STOP finding (PR #295 review `5975211800`): a file needing this module must never
 * attempt it unless ALL THREE hold -- it needs chunking at all, the real (non-synthetic) adapter
 * is actually bound (this module bypasses the adapter to `fetch` its own endpoints directly, so a
 * synthetic/guest session must never reach here), AND the backend has actually constructed
 * ChunkedUploadSessionController for this environment (RouterFactory.php's own
 * $fenzsafeUploadSessionsReady fact, threaded through application context as
 * capabilities.fenzsafe_chunked_upload_ready -- gated behind the installed schema, a configured
 * durable storage provider, AND an explicit local-prototype opt-in env var). Checking only the
 * first two let a real customer's oversized file attempt a chunked upload against a route that
 * was never registered, trading the clean "too large" message for a worse generic failure.
 */
export function shouldAttemptChunkedUpload(fileSizeBytes: number, singleShotMaxBytes: number, adapterReal: boolean, backendReady: boolean): boolean {
  return needsChunkedUpload(fileSizeBytes, singleShotMaxBytes) && adapterReal && backendReady;
}

/** This repo's jsdom test environment has no File.prototype.arrayBuffer() (real browsers do); FileReader is what it does support -- same convention as this codebase's other File-reading code. */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await readFileAsArrayBuffer(file));
  return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('');
}

async function sessionRequest(path: string, init: RequestInit): Promise<{status: number; body: Record<string, unknown>}> {
  const response = await fetch(path, {credentials: 'same-origin', ...init});
  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // Every response this controller returns is JSON, including its error paths -- an
    // unparseable body means something in front of the app (proxy/network error page), not a
    // FenzSafe error code. Fall through with an empty body so throwForSession() still fails
    // closed with a generic message rather than throwing on the JSON parse itself.
  }
  return {status: response.status, body};
}

function throwForSession(body: Record<string, unknown>, fallbackMessage: string, uploadSessionId?: string): never {
  const code = typeof body.code === 'string' ? body.code : 'fenzsafe_upload_session.unknown_error';
  const message = typeof body.message === 'string' ? body.message : fallbackMessage;
  throw new FenzSafeChunkedUploadError(code, message, uploadSessionId);
}

function scopeHeader(expectedScope?: string): Record<string, string> {
  return expectedScope ? {'X-ProFenz-Expected-Scope': expectedScope} : {};
}

/** Best-effort only -- a failed abort leaves an orphaned OPEN session that the server's own lazy-expiry/sweepExpired() maintenance entry point tears down on its own; never surfaced to the caller. */
async function abortSession(uploadSessionId: string, expectedScope?: string): Promise<void> {
  await sessionRequest(`/api/v1/fenzsafe/upload-sessions/${uploadSessionId}/abort`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-ProFenz-CSRF': csrfToken(), ...scopeHeader(expectedScope)},
    body: '{}',
  }).catch(() => undefined);
}

/**
 * Retries commit() on a session that already exists -- the one case worth exposing standalone,
 * since ChunkedUploadSessionService::commit() is deliberately safe to call again on the exact same
 * (already-committed) session id after a scanner outage (503), without re-sending any bytes. See
 * that method's own doc comment. Returns the accepted/quarantined document's id.
 */
export async function retryCommitUploadSession(uploadSessionId: string, expectedScope?: string): Promise<string> {
  const response = await sessionRequest(`/api/v1/fenzsafe/upload-sessions/${uploadSessionId}/complete`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-ProFenz-CSRF': csrfToken(), ...scopeHeader(expectedScope)},
    body: '{}',
  });
  if (response.status !== 200) throwForSession(response.body, 'The upload could not be completed.', uploadSessionId);
  return (response.body.document as {document_id: string}).document_id;
}

/**
 * Uploads one File across many chunk requests and commits it, ending at the same
 * PENDING_SCAN -> accepted/quarantined outcome as registerDocumentBytes(). A failure while
 * appending chunks aborts the session (best-effort); a failure surfaced by commit() itself never
 * does -- a scanner-outage 503 must leave the session retryable (see retryCommitUploadSession()
 * above), and a hard commit failure (integrity mismatch, scan rejection) has nothing left to
 * usefully abort. Never rejects with anything other than FenzSafeChunkedUploadError.
 */
export async function uploadDocumentBytesChunked(documentId: string, expectedVersion: number, file: File, options: ChunkedUploadOptions = {}): Promise<string> {
  const chunkBytes = Math.min(options.chunkBytes ?? FENZSAFE_CHUNK_MAX_BYTES, FENZSAFE_CHUNK_MAX_BYTES);
  const scope = scopeHeader(options.expectedScope);
  const finalSha256 = await sha256Hex(file);

  const startResponse = await sessionRequest(`/api/v1/fenzsafe/documents/${documentId}/upload-sessions`, {
    method: 'POST',
    signal: options.signal,
    headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-ProFenz-CSRF': csrfToken(), ...scope},
    body: JSON.stringify({
      expected_version: expectedVersion,
      content_type: file.type || 'application/octet-stream',
      total_bytes: file.size,
      final_sha256: finalSha256,
    }),
  });
  if (startResponse.status !== 201) throwForSession(startResponse.body, 'The upload session could not be started.');
  const uploadSessionId = (startResponse.body.upload_session as {upload_session_id: string}).upload_session_id;

  try {
    for (let offset = 0; offset < file.size; ) {
      const end = Math.min(offset + chunkBytes, file.size);
      const appendResponse = await sessionRequest(`/api/v1/fenzsafe/upload-sessions/${uploadSessionId}/chunks`, {
        method: 'POST',
        signal: options.signal,
        headers: {'Content-Type': file.type || 'application/octet-stream', 'Accept': 'application/json', 'X-ProFenz-CSRF': csrfToken(), 'X-ProFenz-Chunk-Offset': String(offset), ...scope},
        body: file.slice(offset, end),
      });
      if (appendResponse.status !== 200) throwForSession(appendResponse.body, 'A part of this upload could not be sent.', uploadSessionId);
      offset = end;
      options.onProgress?.({sentBytes: offset, totalBytes: file.size});
    }
  } catch (error) {
    await abortSession(uploadSessionId, options.expectedScope);
    throw error;
  }

  return retryCommitUploadSession(uploadSessionId, options.expectedScope);
}
